import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { calculateDateStats } from './distributionUtils';
import { COLORS } from './constants';
import ExportMenu from './../data/selected_data/export_menu/ExportMenu';

const SubTypeView = ({
    selectedType,
    selectedSubType,
    selectedProducts,
    rawData,
    goBackToTypes,
    goBackToSubTypes
}) => {

    const productsCount = selectedProducts ? selectedProducts.length : 0;
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10; // Number of products per page
    
    // Use useMemo to calculate derived state only when selectedProducts changes
    const { subSubTypeGroups, subSubTypeChartData, dateStats } = useMemo(() => {
        const groups = {};
        if (selectedProducts) {
            selectedProducts.forEach(product => {
                const subSubType = product.substanceSubSubType || 'Not specified';
                groups[subSubType] = (groups[subSubType] || 0) + 1;
            });
        }
        const chartData = Object.entries(groups)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
        
        const stats = calculateDateStats(selectedProducts);
        
        return { subSubTypeGroups: groups, subSubTypeChartData: chartData, dateStats: stats };
    }, [selectedProducts]);
    
    // Filter products based on search query
    const filteredProducts = useMemo(() => {
        if (!selectedProducts) return [];
        const query = searchQuery.toLowerCase();
        return selectedProducts.filter(product =>
            product.name.toLowerCase().includes(query) || product.pnumber.toLowerCase().includes(query)
        );
    }, [selectedProducts, searchQuery]);
    
    // Paginate the filtered products
    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredProducts.slice(startIndex, endIndex);
    }, [filteredProducts, currentPage, pageSize]);

    const totalPages = Math.ceil(filteredProducts.length / pageSize);
    
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    
    const AllData = rawData || [];

        // Create a Set of 'pnumber' values from allProductsForType for efficient lookup
    const pnumbersToKeep = new Set(filteredProducts.map(product => product.pnumber));

    // Filter AllData to keep only items where 'eu_pnumber' is in the Set
    const filteredAllData = AllData.filter(item => pnumbersToKeep.has(item.eu_pnumber));
    
    return (
        <div className="distribution-subtype-view">
            {/* Sticky Navigation Header */}
            <div className="navigation-header sticky-nav">
                <button className="back-button" onClick={goBackToSubTypes}>
                    <span className="back-icon">←</span> Back to {selectedType}
                </button>
                <div className="breadcrumb-trail">
                    <span className="breadcrumb-link" onClick={goBackToTypes}>Overview</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-link" onClick={goBackToSubTypes}>{selectedType}</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="current-breadcrumb">{selectedSubType}</span>
                </div>
            </div>

            <div className="content-below-sticky-nav">
                <div className="page-header">
                    <h2>{selectedType} - {selectedSubType}</h2>
                    <p className="subtitle">Detailed medicine listing and analysis</p>
                </div>

                {/* Subtype Stats */}
                <div className="subtype-stats">
                    <div className="stat-card">
                        <div className="stat-icon">💊</div>
                        <h3>{productsCount}</h3>
                        <p>Medicines</p>
                    </div>
                    {Object.keys(subSubTypeGroups).length > 1 && (
                        <div className="stat-card">
                            <div className="stat-icon">🔀</div>
                            <h3>{Object.keys(subSubTypeGroups).length}</h3>
                            <p>Sub-Subtypes</p>
                        </div>
                    )}
                    {dateStats && (
                        <div className="stat-card highlight-card">
                            <div className="stat-icon">📅</div>
                            <h3>{dateStats.rangeText}</h3>
                            <p>Authorisation Span</p>
                            <span className="sub-stat">{dateStats.earliest} - {dateStats.latest}</span>
                        </div>
                    )}
                </div>

                <div className="stats-summary">
                    <h3>{selectedSubType} Insights</h3>
                    <p>
                        There are <strong>{productsCount}</strong> medicines classified as {selectedType} - {selectedSubType} {}
                        in the database.
                        {Object.keys(subSubTypeGroups).length > 1 && ` These medicines are further categorized into ${Object.keys(subSubTypeGroups).length} sub-subtypes.`}
                        {dateStats && ` The authorisation dates span ${dateStats.rangeText}, from ${dateStats.earliest} to ${dateStats.latest}.`}
                    </p>
                </div>

                {/* Distribution by Sub-Subtype Chart */}
                {subSubTypeChartData.length > 1 && (
                    <div className="chart-container">
                        <div className="chart-header">
                            <h3>Distribution by Sub-Subtype</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={subSubTypeChartData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={150} interval={0} />
                                <Tooltip formatter={(value) => [value, 'Medicines']} />
                                <Legend />
                                <Bar dataKey="value" name="Products" fill="#82ca9d">
                                    {subSubTypeChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[(index + 10) % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Product List */}
                {selectedProducts && selectedProducts.length > 0 ? (
                    <div className="products-list">
                        <div className="products-header">
                            <h3>Medicine List ({productsCount})</h3>
                            <p className="product-instruction" style={{ margin: 0 }}>Click row to view medicine details (opens new tab)</p>
                            <div className="button-container">
                            <ExportMenu selectedData={filteredAllData} text="Export Subtype" />
                            </div>
                        </div>

                        {/* Search Bar */}
                        <input
                            type="text"
                            className="product-search-bar"
                            placeholder="Search medicines..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />

                        <div className="table-container">
                            <table className="products-table">
                                <thead>
                                    <tr>
                                        <th>EU Product Number</th>
                                        <th>Medicine Name</th>
                                        <th>Synthesis Method</th>
                                        <th>Sub-Subtype</th>
                                        <th>Authorisation Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedProducts.map((product, index) => (
                                        <tr
                                            key={`${product.pnumber}-${index}`}
                                            onClick={() => window.open(`/medicine_page/${product.pnumber.replaceAll('/', '_')}`, '_blank')}
                                            className="product-row"
                                        >
                                            <td><span className="product-number">{product.pnumber}</span></td>
                                            <td>{product.name}</td>
                                            <td>{product.synthesisMethod || 'N/A'}</td>
                                            <td><span className="subtype-tag">{product.substanceSubSubType || 'N/A'}</span></td>
                                            <td>{product.authDate ? new Date(product.authDate).toLocaleDateString() : 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        <div className="pagination-controls">
                            <button onClick={goToPreviousPage} disabled={currentPage === 1}>
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button onClick={goToNextPage} disabled={currentPage === totalPages}>
                                Next
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="no-data">
                        <div className="no-data-icon">📭</div>
                        <p>No medicines available for this subtype.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubTypeView;